import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Overflow,
  OverflowItem,
  useOverflowMenu,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { TARGET_TYPE } from './helpers/constants';
import { extractTargetsFromViewModel } from './helpers/utils';
import UrlsImage from './media/url.svg';

/**
 * There's a known bug that fluentui/react-overflow doesn't take gap of the overflow container
 * and margin of overflow item into account and cause weird styling issues.
 * https://github.com/microsoft/fluentui/issues/23066
 */
const useUrlRulesPreviewStyles = makeStyles({
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.margin('8px', '28px'),
    ...shorthands.gap('8px'),
  },
  previewRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    flexWrap: 'nowrap',
    maxWidth: '95%',
    '& .icon': {
      paddingTop: '2px',
    },
    '& .row-label': {
      ...shorthands.padding('2px', '0px', '2px', '0px'),
      textWrap: 'nowrap',
    },
  },
  overflowContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    flexGrow: 1,
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'hidden',
    ...shorthands.gap('4px'),
    '& .tag': {
      flexShrink: 0,
      ...shorthands.padding('2px', '6px', '2px', '6px'),
      ...shorthands.borderRadius('4px'),
      backgroundColor: 'rgba(243, 242, 241, 1)',
      maxWidth: '160px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflowX: 'hidden',
    },
  },
});

const OverflowPlaceholder = () => {
  const { ref, overflowCount, isOverflowing } = useOverflowMenu();
  if (!isOverflowing) {
    return null;
  }
  return (<span ref={ref} className="tag">+{overflowCount}</span>);
};

export const UrlRulesPreview = (props) => {
  const {
    i18n,
    urlRules,
    conditions,
    titleText,
  } = props;

  const styles = useUrlRulesPreviewStyles();

  const {
    urlTargets,
    pageTitleTargets,
    pageContentTargets,
    categoryTargets,
    customLabelTargets,
  } = useMemo(() => extractTargetsFromViewModel(urlRules), [urlRules]);

  const renderPreviewRow = useCallback(({
    iconImg,
    description,
    targets,
  }) => (
    <div className={styles.previewRow}>
      { iconImg && <img className="icon" src={iconImg} alt="Link regular icon" /> }
      { description && <span className="row-label">{i18n.getString(_TL_('{{description}}'), { description })}</span> }
      <Overflow>
        <div className={styles.overflowContainer}>
          { targets.map((target) => {
            const targetString = target.join(', ');
            return (
              <OverflowItem key={targetString} id={targetString}>
                <span className="tag">{targetString}</span>
              </OverflowItem>
            );
          }) }
          <OverflowPlaceholder />
        </div>
      </Overflow>
    </div>
  ), [styles, i18n]);

  const renderPreviewRows = useCallback(() => conditions.map((condition) => {
    switch (condition) {
      case TARGET_TYPE.Url:
        return urlTargets.length > 0 ? renderPreviewRow({ iconImg: UrlsImage, description: 'URL contains', targets: urlTargets }) : null;
      case TARGET_TYPE.PageTitle:
        return pageTitleTargets.length > 0 ? renderPreviewRow({ iconImg: UrlsImage, description: 'Page title is', targets: pageTitleTargets }) : null;
      case TARGET_TYPE.PageContent:
        return pageContentTargets.length > 0 ? renderPreviewRow({ iconImg: UrlsImage, description: 'Page content contains', targets: pageContentTargets }) : null;
      case TARGET_TYPE.Category:
        return categoryTargets.length > 0 ? renderPreviewRow({ iconImg: UrlsImage, description: 'Category is', targets: categoryTargets }) : null;
      case TARGET_TYPE.CustomLabel:
        return customLabelTargets.length > 0 ? renderPreviewRow({ targets: customLabelTargets }) : null;
      default:
        return null;
    }
  }), [categoryTargets, conditions, customLabelTargets, pageContentTargets, pageTitleTargets, renderPreviewRow, urlTargets]);

  return (
    <div className={styles.previewContainer}>
      <span><b>{titleText}</b></span>
      { renderPreviewRows() }
    </div>
  );
};

UrlRulesPreview.propTypes = {
  classes: PropTypes.shape({
    previewContainer: PropTypes.string,
    previewRow: PropTypes.string,
    overflowContainer: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  urlRules: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  conditions: PropTypes.arrayOf(PropTypes.string).isRequired,
  titleText: PropTypes.string.isRequired,
};
